// PDA1072_R01Content.tsx - 비즈니스 로직 (작업지시 기준)
import {
  searchWorkOrders,
  refetchWorkOrders,
  getWorkOrderDetail,
  validateScannedLot,
  createMaterialIssue,
} from "src/apis/materialOutboundOrderApi";
import usePDA1072_R01Store from "./usePDA1072_R01Store";
import type {
  PDA1072_R01HeaderDto,
  PDA1072_R01SearchParams,
  PDA1072_R01Dto,
  PDA1072_R01BomLotDto,
  PDA1072_R01MaterialIssueRequest,
} from "./PDA1072_R01Dto";
import { useToast } from "src/hooks/useToast";

export const usePDA1072_R01Content = () => {
  const {
    searchParams,
    setWorkOrderList,
    setSelectedWorkOrder,
    setWorkOrderDetail,
    setShowList,
    setLoading,
    resetDetail,
    workOrderDetail,
    lotNo,
    setLotNo,
    setScannedBomLot,
    selectedBomLots,
    addSelectedBomLot,
    clearSelectedBomLots,
  } = usePDA1072_R01Store();

  const { showToast } = useToast();

  const buildSearchParams = (): PDA1072_R01SearchParams => {
    const params: PDA1072_R01SearchParams = {};
    if (searchParams.productOrderNo) params.productOrderNo = searchParams.productOrderNo;
    if (searchParams.startDate) params.startDate = searchParams.startDate;
    if (searchParams.endDate) params.endDate = searchParams.endDate;
    if (searchParams.workplaceCd) params.workplaceCd = searchParams.workplaceCd;
    if (searchParams.itemCd) params.itemCd = searchParams.itemCd;
    return params;
  };

  const fetchWorkOrders = async (
    apiFn: (params: PDA1072_R01SearchParams) => Promise<unknown>
  ) => {
    try {
      setLoading(true);
      const params = buildSearchParams();
      const response = await apiFn(params);
      const data = (response as { data?: { data?: PDA1072_R01HeaderDto[] } })?.data?.data ?? [];
      setWorkOrderList(data);
      showToast("success", `작업지시 ${data.length}건 조회됨`);
    } catch (error) {
      console.error("작업지시 조회 실패:", error);
      setWorkOrderList([]);
      showToast("error", "작업지시 조회에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchWorkOrders(searchWorkOrders);
  const handleRefetch = () => fetchWorkOrders(refetchWorkOrders);

  const handleSelectWorkOrder = async (order: PDA1072_R01HeaderDto) => {
    if (!order.productOrderNo) {
      console.error("작업지시 번호가 없습니다.");
      return;
    }
    try {
      setLoading(true);
      const response = await getWorkOrderDetail(order.productOrderNo, order.warehouseCd);
      const detail = (response as { data?: { data?: PDA1072_R01Dto } })?.data?.data;
      if (!detail) throw new Error("작업지시 상세 정보를 찾을 수 없습니다.");
      if (!detail.bomLots?.length) {
        showToast("warning", "출고할 자재가 없습니다.");
        return;
      }
      setSelectedWorkOrder(order);
      setWorkOrderDetail(detail);
      setShowList(false);
    } catch (error: unknown) {
      console.error("작업지시 상세 조회 실패:", error);
      const msg =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showToast("error", msg ?? "작업지시 상세 조회에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  /** Lot.No 스캔 후 Enter: validate-lot API로 검증 후 폼에 표시 */
  const handleScanLot = async () => {
    const trimmed = lotNo.trim();
    if (!trimmed) {
      showToast("error", "LOT 번호를 입력하세요.");
      return;
    }

    const alreadyScanned = selectedBomLots.some((lot) => lot.lotNo === trimmed);
    if (alreadyScanned) {
      showToast("warning", "이미 스캔된 LOT입니다.");
      setLotNo("");
      return;
    }

    const productOrderNo = workOrderDetail?.header?.productOrderNo;
    if (!productOrderNo) {
      showToast("error", "작업지시를 먼저 선택하세요.");
      setLotNo("");
      return;
    }
    try {
      setLoading(true);
      const response = await validateScannedLot(productOrderNo, trimmed);
      const data = (response as { data?: { data?: PDA1072_R01BomLotDto } })?.data?.data;
      if (!data) {
        showToast("error", "LOT 검증 결과를 받지 못했습니다.");
        setLotNo("");
        return;
      }
      if (data.warehouseCd !== "300") {
        showToast("error", "자재창고(300)에 있는 LOT만 출고할 수 있습니다.");
        setLotNo("");
        return;
      }
      // LOT 전량 이동만 허용 → 출고수량 = lot_qty 전량
      const lotQtyNum = Number(data.lotQty) || 0;
      const shipQty = lotQtyNum;
      if (shipQty <= 0) {
        showToast("error", "LOT 재고가 없습니다.");
        setLotNo("");
        return;
      }
      setScannedBomLot(data);
      addSelectedBomLot({
        itemCd: data.itemCd,
        itemNm: data.itemNm ?? "",
        lotNo: data.lotNo ?? trimmed,
        shipQty,
        warehouseCd: data.warehouseCd ?? "",
        locationCd: data.locationCd ?? null,
      });
      setLotNo("");
      showToast("success", `LOT 추가됨 (저장 ${selectedBomLots.length + 1}건)`);
    } catch (error: unknown) {
      const responseData =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { message?: string; code?: string } } }).response?.data
          : undefined;
      const msg = responseData?.message;
      setLotNo("");
      if (responseData?.code === "BOM-005") {
        setScannedBomLot(null);
      }
      showToast("error", msg ?? "LOT 검증에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    const { selectedBomLots: list, clearSelectedBomLots: clear } =
      usePDA1072_R01Store.getState();
    if (list.length > 0) {
      const confirm = window.confirm(
        `스캔된 ${list.length}건의 LOT가 있습니다.\n저장하지 않고 나가시겠습니까?`
      );
      if (!confirm) return;
      clear();
    }
    resetDetail();
    setShowList(true);
  };

  const handleSaveAll = async () => {
    if (!workOrderDetail) {
      showToast("error", "작업지시 정보가 없습니다.");
      return;
    }
    if (selectedBomLots.length === 0) {
      showToast("error", "스캔된 LOT가 없습니다.");
      return;
    }
    try {
      setLoading(true);
      const itemLotMap = new Map<
        string,
        Array<{ lotNo: string; inputOutQty: number; locationCd?: string }>
      >();
      selectedBomLots.forEach((item) => {
        if (!itemLotMap.has(item.itemCd)) itemLotMap.set(item.itemCd, []);
        itemLotMap.get(item.itemCd)!.push({
          lotNo: item.lotNo,
          inputOutQty: item.shipQty,
          locationCd: item.locationCd ?? undefined,
        });
      });
      const itemLotList = Array.from(itemLotMap.entries()).map(([itemCd, list]) => ({
        itemCd,
        lotList: list.map(({ lotNo, inputOutQty, locationCd }) => ({
          lotNo,
          inputOutQty,
          locationCd,
        })),
      }));
      const warehouseCd =
        selectedBomLots[0]?.warehouseCd ?? workOrderDetail.header?.warehouseCd ?? "";
      const request: PDA1072_R01MaterialIssueRequest = {
        productOrderNo: workOrderDetail.header?.productOrderNo ?? "",
        warehouseCd,
        logicCenter: workOrderDetail.header?.logicCenter ?? "",
        remark: "",
        itemLotList,
      };
      await createMaterialIssue(request);
      const totalLots = selectedBomLots.length;
      const totalQty = selectedBomLots.reduce((s, i) => s + i.shipQty, 0);
      showToast(
        "success",
        `자재 출고 완료 (LOT ${totalLots}건, 총 ${totalQty.toLocaleString()}개)`
      );
      clearSelectedBomLots();
      const response = await getWorkOrderDetail(
        workOrderDetail.header?.productOrderNo ?? "",
        warehouseCd
      );
      const raw = (response as { data?: { data?: PDA1072_R01Dto } })?.data?.data;
      if (raw) setWorkOrderDetail(raw);
    } catch (error: unknown) {
      console.error("자재 출고 실패:", error);
      const msg =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showToast("error", msg ?? "자재 출고에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (selectedBomLots.length > 0) {
      const confirm = window.confirm(
        `스캔된 ${selectedBomLots.length}건의 LOT가 있습니다.\n모두 취소하시겠습니까?`
      );
      if (!confirm) return;
      clearSelectedBomLots();
      setScannedBomLot(null);
      setLotNo("");
      showToast("info", "스캔 목록이 초기화되었습니다.");
    }
  };

  return {
    handleSearch,
    handleRefetch,
    handleSelectWorkOrder,
    handleBackToList,
    handleScanLot,
    handleSaveAll,
    handleCancel,
  };
};
